import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import {
    ArrowRight,
    ChevronRight,
    Play,
    CheckCircle2,
} from 'lucide-react';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import api from '../api';
import useSEO from '../hooks/useSEO';

const MeshBackground = () => (
    <div className="bg-mesh"></div>
);

const FloatingParticles = () => (
    <div className="floating-particles fixed inset-0 pointer-events-none z-[-1]">
        {[...Array(12)].map((_, i) => (
            <div
                key={i}
                className="particle"
                style={{
                    left: `${Math.random() * 100}%`,
                    animationDelay: `${Math.random() * 15}s`,
                    animationDuration: `${15 + Math.random() * 15}s`
                }}
            ></div>
        ))}
    </div>
);

const CapabilityTile = ({ icon, title, description, image, delay }) => (
    <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay }}
        className="capability-tile rounded-3xl p-8 flex flex-col overflow-hidden group h-full"
    >
        <div className="mb-6 flex items-center justify-center w-12 h-12 rounded-2xl bg-white/5 border border-white/10 group-hover:border-primary/50 transition-all duration-500">
            <span className="material-symbols-outlined text-primary text-2xl">{icon}</span>
        </div>
        <h3 className="text-white font-display text-xl mb-4 leading-tight">{title}</h3>
        <p className="text-neutral-400 text-sm mb-10 flex-grow leading-relaxed">
            {description}
        </p>
        <div className="mt-auto relative -mb-8 -mx-8 aspect-video overflow-hidden">
            <img
                src={image}
                alt={title}
                className="w-full h-auto object-contain opacity-40 group-hover:opacity-100 transition-all duration-700 transform group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-transparent pointer-events-none"></div>
        </div>
    </motion.div>
);

const USPItem = ({ icon, title, description, delay }) => (
    <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5, delay }}
        className="glass-card p-6 rounded-2xl flex flex-col items-start gap-4 text-left group hover:bg-white/5 transition-all cursor-default"
    >
        <span className="material-symbols-outlined text-primary group-hover:scale-110 transition-transform duration-300" style={{ fontVariationSettings: "'FILL' 1" }}>{icon}</span>
        <div>
            <h4 className="font-display text-sm mb-1 text-white">{title}</h4>
            <p className="text-[10px] text-neutral-400 uppercase tracking-wider">{description}</p>
        </div>
    </motion.div>
);

const capTabs = [
    {
        id: 'Scheduling',
        label: 'Scheduling',
        icon: 'calendar_month',
        tag: 'AUTOMATIC TIMETABLING',
        title: 'Sustained, Algorithm-Grounded Automatic Scheduling',
        description: 'Our mathematical solver maps out complex timetables in seconds, while smart AI tools empower you to make modifications on the fly. Free your deans and coordinators from scheduling headaches.',
        image: '/algorithm_panel.png',
        color: 'from-violet-500 to-indigo-500',
        glowColor: 'rgba(124, 93, 255, 0.15)'
    },
    {
        id: 'Control',
        label: 'E2E Control',
        icon: 'monitoring',
        tag: 'ADMIN CONTROL',
        title: 'Complete E2E Control for Educational Institutions',
        description: 'Take absolute command of your AI settings. Manage model prompts, rate limits, and secure data access across all departments, accelerating overall administrative speed.',
        image: '/dashboard.png',
        color: 'from-fuchsia-500 to-pink-500',
        glowColor: 'rgba(236, 72, 153, 0.15)'
    },
    {
        id: 'AI doubt solver',
        label: 'AI Doubt Solver',
        icon: 'forum',
        tag: 'DOUBT RESOLUTION',
        title: 'Educational Doubt Solver with Expert Escalation',
        description: 'A dedicated platform for users to ask questions safely with rate limits. Save study topics on a personal Notes Board, and reach out to experts for specific, high-level guidance.',
        image: '/AI_Doubt_Solver.png',
        color: 'from-emerald-500 to-teal-500',
        glowColor: 'rgba(16, 185, 129, 0.15)'
    },
    {
        id: 'Content',
        label: 'Content Manager',
        icon: 'database',
        tag: 'CONTENT MANAGER',
        title: 'Convert Any Resource into AI-Ready Knowledge',
        description: 'Upload textbooks, papers, or lecture files. Our custom parsers clean and transform raw materials into precise curriculum nodes, feeding the AI doubt solver with accurate, localized data.',
        image: '/faculty_assignments.png',
        color: 'from-blue-500 to-cyan-500',
        glowColor: 'rgba(59, 130, 246, 0.15)'
    }
];

export default function LandingPage() {
    const navigate = useNavigate();
    const isLoggedIn = !!localStorage.getItem('admin_profile');
    const demoVideoId = "H1vsy5d11Hc";

    useSEO({
        title: 'ScholarsAnchor | AI B2B SaaS for Educational Institutions',
        description: 'ScholarsAnchor is the premier AI B2B SaaS for educational institutions, advancing scheduling optimization, secure academic RAG doubt solvers, and system governance.',
        keywords: 'scholars anchor, scholarsanchor, B2B AI SaaS, AI for educational institutions, academic scheduling, timetabling engine, RAG educational portal, education management software',
        jsonLd: {
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            "name": "ScholarsAnchor",
            "applicationCategory": "EducationalApplication",
            "operatingSystem": "All",
            "offers": {
                "@type": "Offer",
                "price": "0.00",
                "priceCurrency": "USD"
            },
            "description": "High-performance AI B2B SaaS for educational institutions, advancing scheduling optimization and institutional intelligence.",
            "publisher": {
                "@type": "Organization",
                "name": "Threnlabs",
                "url": "https://threnlabs.com"
            }
        }
    });

    const [activeTab, setActiveTab] = useState('Scheduling');
    const activeTabData = capTabs.find((tab) => tab.id === activeTab) || capTabs[0];

    const [textIndex, setTextIndex] = useState(0);
    const phrases = [
        "Complete end-to-end control for educational institutions using secure, smart AI.",
        "Sustained, mathematically-grounded automatic scheduling powered by intelligent tools.",
        "An interactive student dashboard, doubt solver, and content manager in one place.",
        "Encourage academic progress with test-score based credit refueling."
    ];

    useEffect(() => {
        const timer = setInterval(() => {
            setTextIndex((prev) => (prev + 1) % phrases.length);
        }, 4000);
        return () => clearInterval(timer);
    }, [phrases.length]);

    return (
        <div className="min-h-screen bg-[#0A0A0A] text-on-background font-body antialiased selection:bg-primary/30 transition-colors duration-500">
            <MeshBackground />
            <FloatingParticles />
            <Navbar />

            <main className="relative pt-20">
                {/* Hero Section */}
                <section className="relative min-h-[90vh] flex flex-col items-center justify-center text-center px-6 overflow-hidden py-20">
                    <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[800px] h-[800px] glow-overlay pointer-events-none opacity-50"></div>

                    <div className="max-w-5xl z-10">


                        <motion.h1
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.1 }}
                            className="font-display text-center tracking-tighter"
                        >
                            <span className="bg-gradient-to-r from-[#7C5DFF] via-[#CABEFF] to-[#7C5DFF] bg-clip-text text-transparent animate-gradient drop-shadow-[0_0_15px_rgba(124,93,255,0.4)] block">
                                <span className="text-3xl sm:text-4xl md:text-6xl lg:text-7xl font-extrabold block leading-[1.0] md:leading-[0.9] whitespace-nowrap">
                                    One Stop Solution
                                </span>
                                <span className="text-lg sm:text-xl md:text-3xl lg:text-4xl font-medium italic lowercase block leading-none py-1 px-4 my-2 sm:my-3 md:my-4">
                                    for
                                </span>
                                <span className="text-3xl sm:text-4xl md:text-6xl lg:text-7xl font-extrabold block leading-[1.0] md:leading-[0.9] whitespace-nowrap">
                                    Educational Institutions
                                </span>
                            </span>
                        </motion.h1>

                        <div className="h-20 md:h-24 flex items-center justify-center overflow-hidden mb-8">
                            <AnimatePresence mode="wait">
                                <motion.p
                                    key={textIndex}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -20 }}
                                    transition={{ duration: 0.5 }}
                                    className="text-lg md:text-2xl text-neutral-400 max-w-3xl mx-auto font-medium tracking-tight"
                                >
                                    {phrases[textIndex]}
                                </motion.p>
                            </AnimatePresence>
                        </div>

                        <motion.p
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.2 }}
                            className="text-lg md:text-2xl text-primary font-bold tracking-tight mb-12"
                        >
                            Empower your administration, educators, and students with unified AI tools.
                        </motion.p>

                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.3 }}
                            className="flex flex-wrap justify-center gap-6"
                        >
                            <button
                                onClick={() => navigate(isLoggedIn ? '/dashboard/calendar' : '/login')}
                                className="bg-white text-black px-10 py-5 rounded-2xl font-bold text-xl hover:shadow-[0_0_40px_rgba(124,93,255,0.4)] transition-all active:scale-95 flex items-center gap-3"
                            >
                                Get Early Access
                                <ArrowRight className="w-6 h-6" />
                            </button>
                            <button
                                onClick={() => document.getElementById('demo-video')?.scrollIntoView({ behavior: 'smooth' })}
                                className="glass-card text-white px-10 py-5 rounded-2xl font-bold text-xl hover:bg-white/10 transition-all active:scale-95 flex items-center gap-3"
                            >
                                <Play className="w-5 h-5 fill-current" />
                                View Demo
                            </button>
                        </motion.div>
                    </div>

                    {/* USP Floating Cards */}
                    <div className="mt-16 grid grid-cols-1 xs:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl w-full z-10 px-4">
                        <USPItem icon="hub" title="E2E AI Control" description="Total Command & Governance" delay={0.4} />
                        <USPItem icon="edit_calendar" title="Attendance & Records" description="Simple, Secure Logging" delay={0.5} />
                        <USPItem icon="database" title="Content Manager" description="AI-Ready Syllabus Parsers" delay={0.6} />
                        <USPItem icon="sync" title="Automatic Scheduling" description="Constraint-Grounded Solvers" delay={0.7} />
                    </div>
                </section>

                {/* Core Capabilities */}
                <section className="py-20 px-4 md:px-8 max-w-7xl mx-auto relative z-10 text-center">
                    <div className="mb-6 text-left max-w-6xl mx-auto">
                        <span className="text-primary font-bold tracking-[0.4em] uppercase mb-4 block text-sm">Infrastructure</span>
                        <h2 className="font-display text-4xl md:text-6xl text-white tracking-tighter">Core Capabilities</h2>
                    </div>

                    {/* Responsive Text Panel (Mobile only, shown above the monitor) */}
                    <div className="lg:hidden max-w-xl mx-auto text-left mb-10 px-2">
                        <AnimatePresence mode="wait">
                            <motion.div
                                key={activeTab}
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -10 }}
                                transition={{ duration: 0.3 }}
                            >
                                <div className="mb-4">
                                    <span className="text-[9px] sm:text-[10px] font-black uppercase tracking-[0.25em] px-2.5 py-1 rounded-md bg-white/5 border border-white/10 text-primary">
                                        {activeTabData.tag}
                                    </span>
                                </div>
                                <h3 className="font-display text-xl sm:text-2xl text-white font-extrabold tracking-tight mb-4 leading-tight">
                                    {activeTabData.title}
                                </h3>
                                <p className="text-neutral-400 text-xs sm:text-sm leading-relaxed mb-6 font-medium">
                                    {activeTabData.description}
                                </p>
                                <div className="flex items-center gap-3">
                                    <button className="flex items-center gap-1.5 text-xs text-primary font-bold hover:gap-3 transition-all duration-300">
                                        Launch Interface Workspace <ChevronRight className="w-4 h-4" />
                                    </button>
                                </div>
                            </motion.div>
                        </AnimatePresence>
                    </div>

                    {/* iMac Mockup Wrapper */}
                    <div className="relative w-full max-w-6xl mx-auto mt-12">
                        {/* Ambient Glow behind the monitor */}
                        <div 
                            className="absolute -inset-10 blur-[100px] opacity-40 transition-all duration-700 rounded-[3rem] pointer-events-none"
                            style={{
                                background: `radial-gradient(circle, ${activeTabData.glowColor} 0%, transparent 70%)`
                            }}
                        ></div>

                        {/* The Monitor chassis / Browser Mockup */}
                        <div className="relative w-full rounded-xl lg:rounded-2xl bg-zinc-900 border-[4px] sm:border-[8px] lg:border-[10px] border-zinc-800 shadow-[0_20px_50px_rgba(0,0,0,0.5)] lg:shadow-[0_25px_60px_-15px_rgba(0,0,0,0.8)] overflow-hidden">
                            {/* Top glossy reflection bezel */}
                            <div className="absolute inset-x-0 top-0 h-[1px] bg-white/20 z-20"></div>
                            
                            {/* Camera and ambient sensor */}
                            <div className="absolute top-1 sm:top-2 left-1/2 -translate-x-1/2 flex items-center gap-1 sm:gap-1.5 z-30">
                                <div className="w-1 sm:w-1.5 h-1 sm:h-1.5 rounded-full bg-zinc-950 flex items-center justify-center">
                                    <div className="w-0.5 h-0.5 rounded-full bg-[#1e40af]"></div>
                                </div>
                            </div>

                            {/* SCREEN CONTAINER */}
                            <div className="bg-[#0B0B12] w-full min-h-[300px] sm:min-h-[400px] lg:min-h-[580px] relative overflow-hidden flex flex-col">
                                {/* Top Browser Bar & Tabs */}
                                <div className="h-10 sm:h-12 bg-[#141423] border-b border-white/5 flex items-end px-3 sm:px-4 gap-2 sm:gap-4 z-20 select-none">
                                    {/* Windows Dots */}
                                    <div className="flex gap-1.5 pb-3 w-16 hidden sm:flex">
                                        <div className="w-3 h-3 rounded-full bg-[#FF5F56] shadow-sm"></div>
                                        <div className="w-3 h-3 rounded-full bg-[#FFBD2E] shadow-sm"></div>
                                        <div className="w-3 h-3 rounded-full bg-[#27C93F] shadow-sm"></div>
                                    </div>

                                    {/* Browser-style Tab List */}
                                    <div className="flex items-end gap-1 overflow-x-auto scrollbar-none flex-grow flex-nowrap">
                                        {capTabs.map((tab) => {
                                            const isTabActive = tab.id === activeTab;
                                            return (
                                                <button
                                                    key={tab.id}
                                                    onClick={() => setActiveTab(tab.id)}
                                                    className={`flex items-center gap-1.5 sm:gap-2 px-3 py-1.5 sm:px-5 sm:py-2 text-[10px] sm:text-xs font-semibold tracking-wide transition-all duration-200 relative rounded-t-lg border-t-2 border-x flex-shrink-0 whitespace-nowrap ${
                                                        isTabActive
                                                            ? 'bg-[#0B0B12] text-white border-t-primary border-x-white/5 z-10 -mb-[1px]'
                                                            : 'bg-transparent text-neutral-400 border-t-transparent border-x-transparent hover:text-white hover:bg-white/[0.02]'
                                                    }`}
                                                    style={{
                                                        borderBottomColor: isTabActive ? '#0B0B12' : 'transparent'
                                                    }}
                                                >
                                                    <span className="material-symbols-outlined text-[12px] sm:text-[14px] flex-shrink-0" style={{ fontVariationSettings: isTabActive ? "'FILL' 1" : "'FILL' 0" }}>
                                                        {tab.icon}
                                                    </span>
                                                    {tab.label}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>

                                {/* SCREEN VIEWPORT CONTENT */}
                                <div className="flex-grow p-6 sm:p-8 md:p-10 flex flex-col justify-center relative overflow-hidden">
                                    <AnimatePresence mode="wait">
                                        <motion.div
                                            key={activeTab}
                                            initial={{ opacity: 0, y: 15 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            exit={{ opacity: 0, y: -15 }}
                                            transition={{ duration: 0.4 }}
                                            className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center"
                                        >
                                            {/* Text Panel (Desktop only) */}
                                            <div className="hidden lg:flex lg:col-span-5 flex-col text-left">
                                                <div className="mb-4">
                                                    <span className="text-[9px] sm:text-[10px] font-black uppercase tracking-[0.25em] px-2.5 py-1 rounded-md bg-white/5 border border-white/10 text-primary">
                                                        {activeTabData.tag}
                                                    </span>
                                                </div>
                                                <h3 className="font-display text-xl sm:text-2xl md:text-3xl text-white font-extrabold tracking-tight mb-4 leading-tight">
                                                    {activeTabData.title}
                                                </h3>
                                                <p className="text-neutral-400 text-xs sm:text-sm md:text-base leading-relaxed mb-6 font-medium">
                                                    {activeTabData.description}
                                                </p>
                                                <div className="flex items-center gap-3">
                                                    <button className="flex items-center gap-1.5 text-xs text-primary font-bold hover:gap-3 transition-all duration-300">
                                                        Launch Interface Workspace <ChevronRight className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            </div>

                                            {/* Visual Image Panel */}
                                            <div className="col-span-12 lg:col-span-7 relative group/screen">
                                                {/* Inner glowing background */}
                                                <div className="absolute inset-0 bg-gradient-to-tr from-primary/10 to-transparent blur-2xl rounded-2xl opacity-50"></div>
                                                
                                                {/* The screen application snapshot itself */}
                                                <div className="relative rounded-xl border border-white/10 shadow-[0_20px_50px_rgba(0,0,0,0.6)] overflow-hidden bg-black/40 group-hover/screen:border-white/20 transition-all duration-500">
                                                    <img
                                                        src={activeTabData.image}
                                                        alt={activeTabData.title}
                                                        className="w-full h-auto object-cover opacity-90 group-hover/screen:opacity-100 transition-all duration-700 transform group-hover/screen:scale-[1.01]"
                                                    />
                                                    {/* Soft shine reflex overlay */}
                                                    <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/5 to-white/10 pointer-events-none transition-transform duration-1000 transform -translate-x-full group-hover/screen:translate-x-full"></div>
                                                </div>
                                            </div>
                                        </motion.div>
                                    </AnimatePresence>
                                </div>
                            </div>

                            {/* Aluminum bottom chin bar */}
                            <div className="h-6 sm:h-9 lg:h-11 bg-gradient-to-r from-zinc-300 via-zinc-200 to-zinc-300 border-t border-zinc-400/30 flex items-center justify-center relative">
                                {/* Matte Apple-like icon in the center */}
                                <div className="w-3.5 h-3.5 sm:w-4 sm:h-4 lg:w-5 lg:h-5 rounded-full bg-zinc-950 flex items-center justify-center opacity-30 shadow-inner">
                                    <span className="material-symbols-outlined text-zinc-300 text-[8px] sm:text-[10px] lg:text-[12px] font-bold">sync</span>
                                </div>
                            </div>
                        </div>

                        {/* The iMac stand chassis */}
                        <div className="relative z-10 -mt-1.5 flex flex-col items-center">
                            {/* Metal Neck support */}
                            <div 
                                className="w-14 sm:w-20 lg:w-28 h-8 sm:h-12 lg:h-16 bg-gradient-to-b from-zinc-200 to-zinc-400 shadow-md relative"
                                style={{
                                    clipPath: 'polygon(15% 0%, 85% 0%, 100% 100%, 0% 100%)'
                                }}
                            >
                                {/* Inner neck shadow */}
                                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-3.5 h-3.5 sm:w-5 sm:h-5 lg:w-6 lg:h-6 rounded-full bg-black/10 border border-black/5 flex items-center justify-center">
                                    <div className="w-2.5 h-2.5 sm:w-4 sm:h-4 lg:w-5 lg:h-5 bg-zinc-400/10 shadow-inner"></div>
                                </div>
                            </div>
                            {/* Metal Base plate */}
                            <div className="w-24 sm:w-36 lg:w-52 h-1.5 sm:h-2 lg:h-2.5 bg-gradient-to-r from-zinc-300 via-zinc-400 to-zinc-300 rounded-b-xl shadow-lg relative -mt-0.5" />
                            {/* Soft shadow on the floor */}
                            <div className="w-36 sm:w-52 lg:w-72 h-1.5 sm:h-2.5 lg:h-3 bg-black/40 blur-md rounded-full -mt-1 opacity-60"></div>
                        </div>
                    </div>
                </section>

                {/* Intelligent Hub Modules */}
                <section className="py-20 px-8 max-w-7xl mx-auto relative z-10 border-t border-white/5">
                    <div className="mb-12">
                        <span className="text-primary font-bold tracking-[0.4em] uppercase mb-4 block text-sm">Strategic Modules</span>
                        <h2 className="font-display text-4xl md:text-6xl text-white tracking-tighter">The Intelligent Hub</h2>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            className="glass-card p-12 rounded-[3rem] group hover:bg-white/5 transition-all"
                        >
                            <div className="w-16 h-16 rounded-2xl bg-indigo-500/10 border border-indigo-500/20 flex items-center justify-center mb-8 group-hover:scale-110 transition-transform">
                                <span className="material-symbols-outlined text-indigo-400 text-3xl">hub</span>
                            </div>
                            <h3 className="text-3xl font-display text-white mb-6">E2E Admin Command Center</h3>
                            <p className="text-neutral-400 text-lg leading-relaxed mb-10">
                                Complete end-to-end control for your administration. Manage AI settings, customize instructions, and safely accelerate day-to-day coordination from one secure dashboard.
                            </p>
                            <div
                                onClick={() => navigate('/docs#benchrex-portal')}
                                className="flex items-center gap-3 text-indigo-400 font-bold group-hover:gap-5 transition-all cursor-pointer"
                            >
                                Explore Admin Center <ArrowRight className="w-5 h-5" />
                            </div>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: 0.1 }}
                            className="glass-card p-12 rounded-[3rem] group hover:bg-white/5 transition-all"
                        >
                            <div className="w-16 h-16 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center mb-8 group-hover:scale-110 transition-transform">
                                <span className="material-symbols-outlined text-primary text-3xl">edit_calendar</span>
                            </div>
                            <h3 className="text-3xl font-display text-white mb-6">Attendance & Records Logger</h3>
                            <p className="text-neutral-400 text-lg leading-relaxed mb-10">
                                Log student attendance and test score records in one place. Keep institutional progress transparent and let your administrative metrics synchronize automatically.
                            </p>
                            <div
                                onClick={() => navigate('/docs#attendance')}
                                className="flex items-center gap-3 text-primary font-bold group-hover:gap-5 transition-all cursor-pointer"
                            >
                                Explore Records <ArrowRight className="w-5 h-5" />
                            </div>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: 0.2 }}
                            className="glass-card p-12 rounded-[3rem] group hover:bg-white/5 transition-all"
                        >
                            <div className="w-16 h-16 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mb-8 group-hover:scale-110 transition-transform">
                                <span className="material-symbols-outlined text-emerald-400 text-3xl">dashboard</span>
                            </div>
                            <h3 className="text-3xl font-display text-white mb-6">Interactive Student Dashboard</h3>
                            <p className="text-neutral-400 text-lg leading-relaxed mb-10">
                                Give students a personalized study dashboard. Practice tests refuel their AI tokens automatically based on score performance, driving genuine academic engagement.
                            </p>
                            <div
                                onClick={() => navigate('/portal')}
                                className="flex items-center gap-3 text-emerald-400 font-bold group-hover:gap-5 transition-all cursor-pointer"
                            >
                                Explore Student Dashboard <ArrowRight className="w-5 h-5" />
                            </div>
                        </motion.div>
                    </div>
                </section>

                {/* Intelligent Integrations */}
                <section className="py-16 relative overflow-hidden border-y border-white/5 bg-white/[0.02]">
                    <div className="max-w-7xl mx-auto px-8">
                        <div className="text-center mb-16">
                            <h2 className="text-white font-display text-2xl md:text-4xl mb-4 opacity-80">Supported Ecosystem</h2>
                            <p className="text-neutral-400 max-w-2xl mx-auto">Seamlessly integrated with world-class AI models and institutional infrastructure.</p>
                        </div>
                        <div className="flex flex-wrap justify-center gap-12 grayscale opacity-40 hover:grayscale-0 hover:opacity-100 transition-all duration-700">
                            {['OpenAI', 'Anthropic', 'Google Gemini', 'DeepSeek', 'Meta Llama', 'Microsoft 365', 'Google Workspace'].map((brand) => (
                                <div key={brand} className="flex items-center gap-2 group cursor-default">
                                    <span className="text-white font-display font-black text-xl tracking-tighter group-hover:text-primary transition-colors">{brand}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Deep Management Previews */}
                <section className="bg-surface-container-lowest/30 backdrop-blur-sm py-20 relative z-10">
                    <div className="max-w-7xl mx-auto px-8">
                        <div className="flex flex-col md:flex-row justify-between items-end gap-8 mb-12">
                            <div className="max-w-xl">
                                <span className="text-primary font-bold tracking-[0.4em] uppercase mb-4 block text-sm">Platform Interface</span>
                                <h2 className="font-display text-4xl md:text-6xl text-white tracking-tighter">Ecosystem Previews</h2>
                            </div>
                            <p className="text-lg text-neutral-400 max-w-md font-medium tracking-tight">
                                Experience the surgical precision of our high-fidelity interface design.
                            </p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
                            <motion.div
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                className="md:col-span-7 group relative overflow-hidden rounded-[2.5rem] glass-card h-[500px]"
                            >
                                <div className="p-10 z-20 relative">
                                    <h3 className="font-display text-3xl text-white mb-2">E2E AI Control & Governance</h3>
                                    <p className="text-neutral-400 font-medium tracking-tight">Granular control over AI model prompts, student query rate-limits, and school system data logs.</p>
                                </div>
                                <div className="absolute bottom-0 left-0 right-0">
                                    <img
                                        src="/dashboard.png"
                                        alt="Benchrex Portal"
                                        className="w-full h-auto object-contain rounded-t-3xl border-t border-white/10 group-hover:scale-[1.02] transition-all duration-700"
                                    />
                                </div>
                            </motion.div>

                            <motion.div
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: 0.1 }}
                                className="md:col-span-5 group relative overflow-hidden rounded-[2.5rem] glass-card h-[500px]"
                            >
                                <div className="p-10 z-20 relative">
                                    <h3 className="font-display text-3xl text-white mb-2">Content Manager</h3>
                                    <p className="text-neutral-400 font-medium tracking-tight">Custom parsers to parse, clean, and convert raw teaching resources into AI-ready content banks.</p>
                                </div>
                                <div className="absolute bottom-0 left-0 right-0">
                                    <img
                                        src="/faculty_assignments.png"
                                        alt="Faculty UI"
                                        className="w-full h-auto object-contain rounded-t-3xl border-t border-white/10 group-hover:scale-[1.02] transition-all duration-700"
                                    />
                                </div>
                            </motion.div>

                            <motion.div
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: 0.2 }}
                                className="md:col-span-12 group relative overflow-hidden rounded-[3rem] glass-card border-primary/20"
                            >
                                <div className="grid md:grid-cols-2 items-center">
                                    <div className="p-16">
                                        <h3 className="font-display text-5xl text-white mb-6 tracking-tighter">Automatic Scheduling</h3>
                                        <p className="text-neutral-400 text-xl mb-10 font-medium tracking-tight leading-relaxed">
                                            Sustained, algorithm-grounded scheduling <br className="hidden md:block" />
                                            empowered by tools and assistant workspaces.
                                        </p>
                                        <ul className="space-y-6">
                                            {[
                                                "Sustained Algorithmic Timetabling",
                                                "Tool-Empowered Scheduling Assistant",
                                                "Attendance & Test Records Logger"
                                            ].map((text, i) => (
                                                <li key={i} className="flex items-center gap-4 text-white text-lg font-bold group/li">
                                                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center group-hover/li:bg-primary/20 transition-all">
                                                        <CheckCircle2 className="w-5 h-5 text-primary" />
                                                    </div>
                                                    {text}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                    <div className="p-0 relative h-full flex items-center overflow-hidden">
                                        <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A] to-transparent z-10 md:block hidden"></div>
                                        <img
                                            src="/algorithm_panel.png"
                                            alt="Timetabling Engine"
                                            className="w-full h-auto rounded-none shadow-2xl group-hover:scale-[1.02] transition-all duration-700"
                                        />
                                    </div>
                                </div>
                            </motion.div>
                        </div>
                    </div>
                </section>

                {/* Demo Video Section */}
                <section id="demo-video" className="py-20 relative overflow-hidden">
                    <div className="max-w-6xl mx-auto px-6">
                        <motion.div
                            initial={{ opacity: 0, y: 40 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 1 }}
                            className="relative"
                        >
                            <div className="absolute -inset-20 bg-primary/10 blur-[120px] rounded-full pointer-events-none"></div>
                            <div className="relative rounded-[3rem] overflow-hidden border-2 border-white/10 shadow-[0_0_80px_rgba(0,0,0,0.8)] bg-black aspect-video">
                                <iframe
                                    className="w-full h-full"
                                    src={`https://www.youtube.com/embed/${demoVideoId}?rel=0&modestbranding=1`}
                                    title="UIAF Platform Demo"
                                    loading="lazy"
                                    allow="autoplay; encrypted-media; picture-in-picture"
                                    allowFullScreen
                                ></iframe>
                            </div>

                            <div className="absolute -top-6 left-1/2 -translate-x-1/2 px-8 py-3 rounded-full bg-white text-black font-black text-xs uppercase tracking-[0.3em] shadow-2xl z-20">
                                Live Product Walkthrough
                            </div>
                        </motion.div>
                    </div>
                </section>

                <NewsletterSection />
            </main>

            <Footer />
        </div>
    );
}

const NewsletterSection = () => {
    const [email, setEmail] = useState('');
    const [status, setStatus] = useState('idle'); // idle, loading, success, error
    const [message, setMessage] = useState('');

    const handleSubscribe = async (e) => {
        e.preventDefault();
        if (!email) return;

        setStatus('loading');
        try {
            await api.post('/api/newsletter/subscribe', { email });
            setStatus('success');
            setMessage('Thank you for subscribing to our newsletter!');
            setEmail('');
            setTimeout(() => setMessage(''), 5000);
        } catch (err) {
            setStatus('error');
            setMessage(err.response?.data?.detail || 'Failed to subscribe. Please try again.');
            setTimeout(() => {
                setStatus('idle');
                setMessage('');
            }, 5000);
        }
    };

    return (
        <section className="py-24 px-6 relative overflow-hidden z-10 border-t border-white/5 bg-white/[0.01]">
            <div className="max-w-4xl mx-auto text-center">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                >
                    <h2 className="font-display text-3xl md:text-5xl text-white mb-6 tracking-tighter">Stay Ahead with UIAF Updates</h2>
                    <p className="text-lg text-neutral-400 mb-10 max-w-2xl mx-auto">
                        Get the latest insights on AI governance, institutional scheduling, and platform updates delivered straight to your inbox.
                    </p>

                    <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-4 max-w-lg mx-auto">
                        <input
                            type="email"
                            placeholder="your@email.com"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                            className="flex-grow bg-white/5 border border-white/10 rounded-xl px-6 py-4 text-white focus:outline-none focus:border-primary/50 transition-all"
                        />
                        <button
                            type="submit"
                            disabled={status === 'loading'}
                            className="bg-primary text-white px-8 py-4 rounded-xl font-bold hover:bg-primary/80 transition-all active:scale-95 disabled:opacity-50 flex items-center justify-center gap-2 min-w-[140px]"
                        >
                            {status === 'loading' ? (
                                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                            ) : (
                                <>Subscribe <ArrowRight className="w-5 h-5" /></>
                            )}
                        </button>
                    </form>

                    <AnimatePresence>
                        {message && (
                            <motion.p
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0 }}
                                className={`mt-4 text-sm font-medium ${status === 'success' ? 'text-emerald-400' : 'text-red-400'}`}
                            >
                                {message}
                            </motion.p>
                        )}
                    </AnimatePresence>
                </motion.div>
            </div>
        </section>
    );
};
